import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Save, RotateCcw, Loader2, Brain, ChevronDown, ChevronUp } from 'lucide-react';

const API = '';

/**
 * Card component for viewing + editing the Orchestrator System Prompt.
 *
 * This is the top-level reasoning prompt that drives every Orka AI request.
 * Users can customise it; "Reset to default" restores the shipped baseline.
 */
export function OrchestratorPromptCard() {
  const [data, setData] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showDefault, setShowDefault] = useState(false);

  const fetchPrompt = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ai/orchestrator/prompt`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setContent(d.effective || '');
      }
    } catch (e) { console.error('prompt fetch error', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPrompt(); }, [fetchPrompt]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ai/orchestrator/prompt`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        if ((e.detail || '').includes('too_short')) {
          toast.error('Prompt is too short — needs at least 50 characters.');
        } else if ((e.detail || '').includes('too_long')) {
          toast.error('Prompt is too long — max 30 000 characters.');
        } else {
          toast.error(e.detail || 'Save failed');
        }
      } else {
        toast.success('Orchestrator prompt saved — applies to the next AI request');
        const d = await res.json();
        setData(d);
      }
    } catch (e) { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset the Orchestrator prompt to the Orka default? Your custom prompt will be lost.')) return;
    setResetting(true);
    try {
      const res = await fetch(`${API}/api/ai/orchestrator/prompt/reset`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setContent(d.effective);
        toast.success('Reverted to Orka default');
      } else { toast.error('Reset failed'); }
    } catch (e) { toast.error('Reset error'); }
    finally { setResetting(false); }
  };

  const isDirty = data && content !== (data.effective || '');

  return (
    <Card data-testid="orchestrator-prompt-card">
      <CardHeader className="py-4 px-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Brain className="w-5 h-5 text-violet-500" />
              Orchestrator System Prompt
              {data?.is_customized && (
                <Badge variant="default" className="text-[10px] bg-violet-500" data-testid="orchestrator-customized-badge">
                  Customised
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1">
              The reasoning prompt Orka AI runs before every request. Tweak it to change how the AI plans, clarifies, and picks capabilities.
            </CardDescription>
          </div>
          <Button
            variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}
            data-testid="orchestrator-expand-btn"
          >
            {expanded ? <><ChevronUp className="w-4 h-4 mr-1" />Collapse</> : <><ChevronDown className="w-4 h-4 mr-1" />Edit</>}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="p-5 space-y-3">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {data?.updated_at && (
                <p className="text-[11px] text-muted-foreground">
                  Last edited {new Date(data.updated_at).toLocaleString()} by {data.updated_by}
                </p>
              )}

              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={18}
                className="text-[11px] font-mono"
                spellCheck={false}
                data-testid="orchestrator-prompt-textarea"
              />

              <div className="flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">
                  {content.length.toLocaleString()} characters
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost" size="sm" onClick={() => setShowDefault((v) => !v)}
                    data-testid="orchestrator-show-default-btn"
                  >
                    {showDefault ? 'Hide default' : 'Show default'}
                  </Button>
                  {data?.is_customized && (
                    <Button
                      variant="outline" size="sm" onClick={reset} disabled={resetting}
                      data-testid="orchestrator-reset-btn"
                    >
                      {resetting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                      Reset to default
                    </Button>
                  )}
                  <Button
                    size="sm" onClick={save} disabled={saving || !isDirty}
                    data-testid="orchestrator-save-btn"
                  >
                    {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                    Save
                  </Button>
                </div>
              </div>

              {showDefault && (
                <div className="mt-2 p-3 bg-muted/50 rounded-lg border space-y-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Orka shipped default
                  </span>
                  <pre className="text-[10px] whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">
                    {data?.default}
                  </pre>
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
